import React, { useState, useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'gatsby';

import Logo from '../Logo';
import Container from '../Container';
import Nav from './Nav';
import ToggleBtn from './ToggleBtn';
import SocialBranding from '../SocialMedia/SocialBranding';
import HeaderSearchForm from '../SearchForm/headerSearch';
import useOnClickOutside from './OnClickOutside';
import CallToAction from '../CallToAction';
import { theme } from '../../styles/GlobalStyles';

// eslint-disable-next-line import/no-cycle
import { BlogPostContext } from '../../templates/blog-post';

// eslint-disable-next-line import/no-cycle
import { HelpArticleContext } from '../../templates/helpArticleTemplate';
import { isHelpCenterSection, isBlogSection, isAuthorSection } from '../../utils/pageURLCheck';

import {
  NavBarWrapper,
  NavBrand,
  NavWrapper,
  NavControls,
  Progress,
  NavLinkExternal,
} from './HeaderStyles';

const NavBar = ({
  scrollPosition,
  helpNavScroll,
  children,
  mobile,
  isOpen,
  searchOpen,
  pathname,
}) => (
  <NavBarWrapper mobile={mobile} helpNavScroll={helpNavScroll}>
    <Container>
      <NavWrapper>
        <NavBrand helpNavScroll={helpNavScroll} isOpen={isOpen} searchOpen={searchOpen}>
          <div>
            <Link to="/">
              <Logo />
              <b>Workast</b>
            </Link>
            {isBlogSection(pathname) || isAuthorSection(pathname) ? (<Link to="/blog">&nbsp;| Blog </Link>) : (<></>)}
            {isHelpCenterSection(pathname) ? (<Link to="/help">&nbsp;| Help Center </Link>) : (<></>)}
          </div>
        </NavBrand>
        {children}
      </NavWrapper>
    </Container>
    <Progress scroll={`${scrollPosition}%`} />
  </NavBarWrapper>
);

export const DesktopNavigation = ({
  scrollPosition,
  helpNavScroll,
  scrolled,
  menu,
  pathname,
}) => {
  const ref = useRef();
  const { title: blogTitle } = useContext(BlogPostContext);
  const { title: articleTitle } = useContext(HelpArticleContext);
  const [searchOpen, setSearchOpen] = useState(false);
  useOnClickOutside(ref, () => setSearchOpen(false));

  return (
    <NavBar scrollPosition={scrollPosition} helpNavScroll={helpNavScroll} pathname={pathname}>
      <Nav
        searchOpen={searchOpen}
        scrolled={scrolled}
        helpNavScroll={helpNavScroll}
        menu={menu}
        pathname={pathname}
        body={isBlogSection(pathname) || isAuthorSection(pathname) ? blogTitle : articleTitle}
      />
      <div ref={ref}>
        {isHelpCenterSection(pathname) ? (
          <HeaderSearchForm
            helpNavScroll={helpNavScroll}
            open={searchOpen}
            setOpen={setSearchOpen}
          />
        ) : (
          <></>
        )}
      </div>
      <NavControls>
        <ul>
          <li>
            <NavLinkExternal
              target="_blank"
              rel="noopener noreferrer"
              href="https://my.workast.io/?_ga=2.60086957.1258453354.1597084599-1572877097.1596510761"
            >
              Log In
            </NavLinkExternal>
          </li>
          <li>
            <CallToAction text="Sign up" />
          </li>
        </ul>
      </NavControls>
    </NavBar>
  );
};

export const MobileNavigation = ({
  scrolled,
  helpNavScroll,
  menu,
  scrollPosition,
  pathname,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const toggle = () => setIsOpen(!isOpen);
  const ref = useRef();
  const [searchOpen, setSearchOpen] = useState(false);
  useOnClickOutside(ref, () => setSearchOpen(false));

  return (
    <NavBar
      scrollPosition={scrollPosition}
      searchOpen={searchOpen}
      pathname={pathname}
      isOpen={isOpen}
      helpNavScroll={helpNavScroll}
      mobile
    >
      <Nav
        isOpen={isOpen}
        scrolled={scrolled}
        searchOpen={searchOpen}
        helpNavScroll={helpNavScroll}
        menu={menu}
        pathname={pathname}
      >
        <>
          <ul>
            <li>
              <NavLinkExternal
                target="_blank"
                rel="noopener noreferrer"
                href="https://my.workast.io/?_ga=2.60086957.1258453354.1597084599-1572877097.1596510761"
              >
                Log In
              </NavLinkExternal>
            </li>
            <li>
              <CallToAction text="Sign up" />
            </li>
            <li>
              <SocialBranding
                color={theme.icon.whiteIcon}
                list={['youtube', 'twitter', 'linkedIn']}
              />
            </li>
          </ul>
        </>
      </Nav>
      <div ref={ref}>
        {isHelpCenterSection(pathname) ? (
          <HeaderSearchForm
            open={searchOpen}
            setOpen={setSearchOpen}
            toggleOpen={isOpen}
            helpNavScroll={helpNavScroll}
          />
        ) : (
          <></>
        )}
      </div>
      <ToggleBtn toggle={toggle} isOpen={isOpen} helpNavScroll={helpNavScroll} />
    </NavBar>
  );
};

NavBar.propTypes = {
  pathname: PropTypes.string.isRequired,
  helpNavScroll: PropTypes.number,
  mobile: PropTypes.bool,
  isOpen: PropTypes.bool,
  searchOpen: PropTypes.bool,
  scrollPosition: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  children: PropTypes.arrayOf(PropTypes.element).isRequired,
};

NavBar.defaultProps = {
  scrollPosition: false,
  helpNavScroll: 500,
  mobile: false,
  isOpen: false,
  searchOpen: false,
};

DesktopNavigation.propTypes = {
  pathname: PropTypes.string.isRequired,
  helpNavScroll: PropTypes.number,
  scrolled: PropTypes.bool,
  scrollPosition: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  menu: PropTypes.shape({
    name: PropTypes.string,
    links: PropTypes.arrayOf(
      PropTypes.shape({
        url: PropTypes.string,
        name: PropTypes.string,
      })
    ),
  }).isRequired,
};

DesktopNavigation.defaultProps = {
  scrolled: false,
  helpNavScroll: 500,
  scrollPosition: false,
};

MobileNavigation.propTypes = {
  pathname: PropTypes.string.isRequired,
  helpNavScroll: PropTypes.number,
  scrolled: PropTypes.bool,
  scrollPosition: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  menu: PropTypes.shape({
    name: PropTypes.string,
    links: PropTypes.arrayOf(
      PropTypes.shape({
        url: PropTypes.string,
        name: PropTypes.string,
      })
    ),
  }).isRequired,
};

MobileNavigation.defaultProps = {
  scrolled: false,
  helpNavScroll: 500,
  scrollPosition: false,
};

export default { DesktopNavigation, MobileNavigation };
